import { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Save, X, Search, Plane, FileText, AlertTriangle, Brain, TrendingUp, Users, MapPin, BarChart3, CheckCircle, XCircle, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Simple password protection (should be replaced with proper auth in production)
const ADMIN_PASSWORD = 'petfly2024';

export default function AdminDashboard() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('dataBrain');
  
  // Rules state
  const [rules, setRules] = useState([]);
  const [airlines, setAirlines] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAirline, setSelectedAirline] = useState('');
  const [editingRule, setEditingRule] = useState(null);
  const [newRule, setNewRule] = useState({ airline_code: '', category: 'Crate', rule: '', source_url: '' });
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({ totalRules: 0, totalAirlines: 0, categories: {} });
  
  // Analytics (Data Brain)
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && activeTab === 'dataBrain' && !analytics) {
      fetchAnalytics();
    }
  }, [isAuthenticated, activeTab]);

  const fetchAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      const [analyticsRes, trendsRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/analytics/trends`)
      ]);
      setAnalytics(analyticsRes.data);
      setTrends(trendsRes.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      toast.error('Failed to load analytics data');
    }
    setAnalyticsLoading(false);
  };

  const fetchData = async () => {
    try {
      const [rulesRes, airlinesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/rules`),
        axios.get(`${API}/admin/airlines`),
        axios.get(`${API}/admin/stats`)
      ]);
      setRules(rulesRes.data);
      setAirlines(airlinesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success('Welcome to Admin Dashboard');
    } else {
      toast.error('Invalid password');
    }
  };

  const handleAddRule = async () => {
    if (!newRule.airline_code || !newRule.rule) {
      toast.error('Please fill airline code and rule');
      return;
    }
    try {
      await axios.post(`${API}/admin/rules`, newRule);
      toast.success('Rule added successfully');
      setNewRule({ airline_code: '', category: 'Crate', rule: '', source_url: '' });
      setShowAddForm(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add rule');
    }
  };

  const handleUpdateRule = async (rule) => {
    try {
      await axios.put(`${API}/admin/rules/${rule._id}`, rule);
      toast.success('Rule updated');
      setEditingRule(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    try {
      await axios.delete(`${API}/admin/rules/${ruleId}`);
      toast.success('Rule deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const filteredRules = rules.filter(rule => {
    const matchesSearch = rule.rule.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rule.airline_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesAirline = !selectedAirline || rule.airline_code === selectedAirline;
    return matchesSearch && matchesAirline;
  });

  const categories = ['Crate', 'Documents', 'Restrictions', 'Temperature', 'Dimensions', 'Health', 'Other'];

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center flex items-center justify-center gap-2">
              <Plane className="w-6 h-6 text-[#0066CC]" />
              PetFlyCheck Admin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-12"
              />
              <Button type="submit" className="w-full h-12 bg-[#0066CC] hover:bg-[#0052A3]">
                Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Plane className="w-8 h-8 text-[#0066CC]" />
            <h1 className="text-xl font-bold text-slate-900">PetFlyCheck Admin</h1>
          </div>
          <Button variant="outline" onClick={() => setIsAuthenticated(false)}>
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalRules}</p>
                  <p className="text-sm text-slate-500">Total Rules</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Plane className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.totalAirlines}</p>
                  <p className="text-sm text-slate-500">Airlines</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.categories?.Restrictions || 0}</p>
                  <p className="text-sm text-slate-500">Restrictions</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900">{stats.categories?.Documents || 0}</p>
                  <p className="text-sm text-slate-500">Document Rules</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={activeTab === 'dataBrain' ? 'default' : 'outline'}
            onClick={() => setActiveTab('dataBrain')}
            className={activeTab === 'dataBrain' ? 'bg-gradient-to-r from-purple-600 to-blue-600' : ''}
          >
            <Brain className="w-4 h-4 mr-2" />
            Data Brain
          </Button>
          <Button
            variant={activeTab === 'rules' ? 'default' : 'outline'}
            onClick={() => setActiveTab('rules')}
            className={activeTab === 'rules' ? 'bg-[#0066CC]' : ''}
          >
            IATA Rules
          </Button>
          <Button
            variant={activeTab === 'airlines' ? 'default' : 'outline'}
            onClick={() => setActiveTab('airlines')}
            className={activeTab === 'airlines' ? 'bg-[#0066CC]' : ''}
          >
            Airlines
          </Button>
        </div>

        {/* Data Brain Tab */}
        {activeTab === 'dataBrain' && (
          <div className="space-y-6">
            {analyticsLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
              </div>
            ) : analytics ? (
              <>
                {/* Header */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-blue-600 rounded-xl flex items-center justify-center">
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-slate-900">Data Brain</h2>
                      <p className="text-sm text-slate-500">AI-powered insights from all analyses</p>
                    </div>
                  </div>
                  <Button onClick={fetchAnalytics} variant="outline" size="sm">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Refresh
                  </Button>
                </div>

                {/* Key Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-100 text-sm">Total Analyses</p>
                          <p className="text-3xl font-bold mt-1">{analytics.total_analyses}</p>
                        </div>
                        <BarChart3 className="w-10 h-10 text-blue-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-100 text-sm">Compliance Rate</p>
                          <p className="text-3xl font-bold mt-1">{analytics.compliance_rate}%</p>
                        </div>
                        <CheckCircle className="w-10 h-10 text-green-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-amber-500 to-orange-500 text-white border-0">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-amber-100 text-sm">Cage Check ($39)</p>
                          <p className="text-3xl font-bold mt-1">{analytics.plan_distribution?.cage_check || 0}</p>
                        </div>
                        <DollarSign className="w-10 h-10 text-amber-200" />
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-purple-100 text-sm">Full Audit ($89)</p>
                          <p className="text-3xl font-bold mt-1">{analytics.plan_distribution?.full_audit || 0}</p>
                        </div>
                        <DollarSign className="w-10 h-10 text-purple-200" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Status Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-green-200 bg-green-50">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-8 h-8 text-green-600" />
                        <div>
                          <p className="text-sm text-green-700">Compliant (OK)</p>
                          <p className="text-2xl font-bold text-green-800">{analytics.status_breakdown?.OK || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-8 h-8 text-amber-600" />
                        <div>
                          <p className="text-sm text-amber-700">With Warnings</p>
                          <p className="text-2xl font-bold text-amber-800">{analytics.status_breakdown?.WARNING || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-5">
                      <div className="flex items-center gap-3">
                        <XCircle className="w-8 h-8 text-red-600" />
                        <div>
                          <p className="text-sm text-red-700">Non-Compliant</p>
                          <p className="text-2xl font-bold text-red-800">{analytics.status_breakdown?.FAIL || 0}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Detailed Tables */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Common Failures */}
                  <Card>
                    <CardHeader className="border-b bg-red-50">
                      <CardTitle className="text-lg flex items-center gap-2 text-red-800">
                        <AlertTriangle className="w-5 h-5" />
                        Most Common Issues
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      {analytics.common_failures?.length > 0 ? (
                        <table className="w-full">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left p-3 text-sm font-medium text-slate-600">Issue</th>
                              <th className="text-right p-3 text-sm font-medium text-slate-600">Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.common_failures.map((item, idx) => (
                              <tr key={idx} className="border-b hover:bg-slate-50">
                                <td className="p-3 text-sm text-slate-700">{item.criteria}</td>
                                <td className="p-3 text-right">
                                  <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-sm font-medium">
                                    {item.count}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="p-8 text-center text-slate-500">No failure data yet</div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Top Routes */}
                  <Card>
                    <CardHeader className="border-b bg-blue-50">
                      <CardTitle className="text-lg flex items-center gap-2 text-blue-800">
                        <MapPin className="w-5 h-5" />
                        Top Routes
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      {analytics.top_routes?.length > 0 ? (
                        <table className="w-full">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left p-3 text-sm font-medium text-slate-600">Route</th>
                              <th className="text-right p-3 text-sm font-medium text-slate-600">Analyses</th>
                            </tr>
                          </thead>
                          <tbody>
                            {analytics.top_routes.map((item, idx) => (
                              <tr key={idx} className="border-b hover:bg-slate-50">
                                <td className="p-3 text-sm text-slate-700 font-medium">{item.route}</td>
                                <td className="p-3 text-right">
                                  <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">
                                    {item.count}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="p-8 text-center text-slate-500">No route data yet</div>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* Airline Stats */}
                <Card>
                  <CardHeader className="border-b">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Plane className="w-5 h-5 text-[#0066CC]" />
                      Airlines Analyzed
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-4">
                    {analytics.airline_stats?.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {analytics.airline_stats.map((item, idx) => (
                          <div 
                            key={idx} 
                            className="px-3 py-2 bg-slate-100 rounded-lg flex items-center gap-2 hover:bg-slate-200 transition-colors"
                          >
                            <span className="font-bold text-slate-700">{item.airline}</span>
                            <span className="text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full">{item.count}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-slate-500">No airline data yet</div>
                    )}
                  </CardContent>
                </Card>

                {/* Daily Trends Chart */}
                {trends?.daily_trends?.length > 0 && (
                  <Card>
                    <CardHeader className="border-b">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        Daily Analysis Trends (Last 30 Days)
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4">
                      <div className="flex items-end gap-1 h-40">
                        {trends.daily_trends.map((day, idx) => {
                          const maxCount = Math.max(...trends.daily_trends.map(d => d.count));
                          const height = maxCount > 0 ? (day.count / maxCount) * 100 : 0;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                              <div 
                                className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all hover:from-blue-700 hover:to-blue-500"
                                style={{ height: `${Math.max(height, 2)}%` }}
                                title={`${day.date}: ${day.count} analyses`}
                              ></div>
                              {idx % 5 === 0 && (
                                <span className="text-xs text-slate-400 transform -rotate-45 origin-left">
                                  {day.date.slice(5)}
                                </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="p-12 text-center">
                  <Brain className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-slate-700 mb-2">No Analytics Data Yet</h3>
                  <p className="text-slate-500">Analytics will appear here once users start using PetFlyCheck.</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Rules Tab */}
        {activeTab === 'rules' && (
          <Card>
            <CardHeader className="border-b">
              <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <CardTitle>IATA Rules Management</CardTitle>
                <div className="flex flex-col md:flex-row gap-2 w-full md:w-auto">
                  <div className="relative flex-1 md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search rules..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <select
                    value={selectedAirline}
                    onChange={(e) => setSelectedAirline(e.target.value)}
                    className="h-10 px-3 border border-slate-200 rounded-md bg-white"
                  >
                    <option value="">All Airlines</option>
                    {[...new Set(rules.map(r => r.airline_code))].sort().map(code => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </select>
                  <Button onClick={() => setShowAddForm(true)} className="bg-[#0066CC] hover:bg-[#0052A3]">
                    <Plus className="w-4 h-4 mr-2" /> Add Rule
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Add Rule Form */}
              {showAddForm && (
                <div className="p-4 bg-blue-50 border-b">
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <Input
                      placeholder="Airline Code (e.g., AA)"
                      value={newRule.airline_code}
                      onChange={(e) => setNewRule({...newRule, airline_code: e.target.value.toUpperCase()})}
                    />
                    <select
                      value={newRule.category}
                      onChange={(e) => setNewRule({...newRule, category: e.target.value})}
                      className="h-10 px-3 border border-slate-200 rounded-md bg-white"
                    >
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <Input
                      placeholder="Rule description"
                      value={newRule.rule}
                      onChange={(e) => setNewRule({...newRule, rule: e.target.value})}
                      className="md:col-span-2"
                    />
                    <div className="flex gap-2">
                      <Button onClick={handleAddRule} className="bg-green-600 hover:bg-green-700 flex-1">
                        <Save className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" onClick={() => setShowAddForm(false)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Rules Table */}
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Airline</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Category</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600">Rule</th>
                      <th className="text-left p-3 text-sm font-medium text-slate-600 w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRules.map((rule, idx) => (
                      <tr key={rule._id || idx} className="border-b hover:bg-slate-50">
                        <td className="p-3">
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">
                            {rule.airline_code}
                          </span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-sm ${
                            rule.category === 'Restrictions' ? 'bg-red-100 text-red-700' :
                            rule.category === 'Documents' ? 'bg-purple-100 text-purple-700' :
                            rule.category === 'Crate' ? 'bg-green-100 text-green-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {rule.category}
                          </span>
                        </td>
                        <td className="p-3 text-sm text-slate-700">
                          {editingRule?._id === rule._id ? (
                            <Input
                              value={editingRule.rule}
                              onChange={(e) => setEditingRule({...editingRule, rule: e.target.value})}
                              className="h-8"
                            />
                          ) : (
                            rule.rule
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-1">
                            {editingRule?._id === rule._id ? (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => handleUpdateRule(editingRule)}>
                                  <Save className="w-4 h-4 text-green-600" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingRule(null)}>
                                  <X className="w-4 h-4 text-slate-400" />
                                </Button>
                              </>
                            ) : (
                              <>
                                <Button size="sm" variant="ghost" onClick={() => setEditingRule(rule)}>
                                  <Edit2 className="w-4 h-4 text-slate-400" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteRule(rule._id)}>
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {filteredRules.length === 0 && (
                <div className="p-8 text-center text-slate-500">
                  No rules found matching your criteria
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Airlines Tab */}
        {activeTab === 'airlines' && (
          <Card>
            <CardHeader className="border-b">
              <CardTitle>Airlines with Rules</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 p-4">
                {[...new Set(rules.map(r => r.airline_code))].sort().map(code => {
                  const count = rules.filter(r => r.airline_code === code).length;
                  return (
                    <div
                      key={code}
                      onClick={() => { setSelectedAirline(code); setActiveTab('rules'); }}
                      className="p-3 border rounded-lg cursor-pointer hover:border-[#0066CC] hover:bg-blue-50 transition-colors"
                    >
                      <p className="font-bold text-lg text-slate-900">{code}</p>
                      <p className="text-sm text-slate-500">{count} rules</p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
